import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FUNNEL_STAGES,
  HelpTip,
  cn,
  formatDate,
  PersonDisplay,
} from '@dataroom/shared';
import type { BuyerEngagementSummary, FunnelStage, LoiReviewItem } from '@dataroom/shared';
import {
  PIPELINE_SEGMENTS,
  buyerLabel,
  buyerStateLine,
  latestLoiByBuyer,
  sortPipeline,
  stageLine,
  stageNumber,
} from '@/lib/buyer-pipeline';

interface BuyerPipelineCardProps {
  /** The room's buyers — the engagement read (funnel rows + buyer-side grants;
   * a buyer before the NDA is at Teaser). */
  buyers: BuyerEngagementSummary[];
  /** The room's LOIs (each buyer's latest gives the LOI state line). */
  lois: LoiReviewItem[];
  loading: boolean;
  error: string | null;
  /** Open that buyer's detail. */
  onSelectBuyer: (actorId: string) => void;
}

/** The 7-segment stage bar, filled up to the buyer's current stage. Spans only:
 * it sits inside the row's button (phrasing content). The row's label carries
 * the same fact as text ("Stage N of 7: …"). */
export function StageBar({ stage }: { stage: FunnelStage }) {
  const reached = stageNumber(stage);
  return (
    <span className="grid grid-cols-7 gap-1" aria-hidden="true" data-stage-bar={stage}>
      {FUNNEL_STAGES.map((s, i) => {
        const done = i < reached;
        const current = i === reached - 1;
        return (
          <span key={s} className="min-w-0 space-y-1" data-reached={done || undefined}>
            <span
              className={cn('block h-1.5 rounded-full', done ? 'bg-primary' : 'bg-muted')}
            />
            <span
              className={cn(
                'block truncate text-center text-[9px] font-semibold uppercase tracking-wide sm:text-[10px]',
                current ? 'text-ink' : done ? 'text-ink-2' : 'text-ink-3',
              )}
            >
              {PIPELINE_SEGMENTS[s]}
            </span>
          </span>
        );
      })}
    </span>
  );
}

/**
 * The buyer pipeline (owner ask 2026-10-07): every buyer of the room on one
 * card — where each stands in the 7-step funnel, a one-line state read off the
 * recorded facts (stage, latest LOI status, access ended, documents opened) and
 * their last activity. Furthest stage first, then most recent activity: the
 * order of the funnel itself, not a ranking or a score. A row opens the buyer's
 * detail.
 */
export function BuyerPipelineCard({
  buyers,
  lois,
  loading,
  error,
  onSelectBuyer,
}: BuyerPipelineCardProps) {
  const latest = latestLoiByBuyer(lois);
  const rows = sortPipeline(buyers);
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Buyer pipeline <HelpTip id="admin.buyers.pipeline" className="ml-1" />
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Where each buyer stands, furthest along first. Open a buyer for their access and
          activity.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : rows.length > 0 ? (
          <ul className="divide-y">
            {rows.map((b) => {
              const state = buyerStateLine(b, latest.get(b.actorId));
              const who = buyerLabel(b);
              return (
                <li key={b.actorId}>
                  <button
                    type="button"
                    onClick={() => onSelectBuyer(b.actorId)}
                    aria-label={`${who}: ${state}. ${stageLine(b.stage)}. Open buyer`}
                    className="grid w-full gap-3 rounded-md px-2 py-3 text-left transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] md:items-center md:gap-6"
                  >
                    <span className="min-w-0 space-y-1">
                      <PersonDisplay
                        name={b.actorName}
                        email={b.actorEmail}
                        fallback={b.actorId}
                        size="sm"
                      />
                      <span
                        className={cn(
                          'block text-[13px] font-medium',
                          b.accessEnded ? 'text-ink-3' : 'text-ink',
                        )}
                      >
                        {state}
                      </span>
                    </span>
                    <span className="min-w-0 space-y-1.5">
                      <StageBar stage={b.stage} />
                      <span className="flex flex-wrap justify-between gap-x-3 text-xs text-ink-2">
                        <span>{stageLine(b.stage)}</span>
                        <span className="text-ink-3">
                          {b.lastActiveAt
                            ? `Last active ${formatDate(b.lastActiveAt)}`
                            : 'No activity yet'}
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            No buyers yet. Invite one from Access and they show here at Teaser.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
